import { PencilSimple, Trash } from '@phosphor-icons/react';
import { addDays, type Note } from '@shh/shared';
import { Button, NoteCard, Sheet, Text } from '@shh/ui';
import { useState } from 'react';
import { useLens } from '../app/lens';
import { deleteNote } from '../data/api';
import { dayLabel } from '../data/agenda';
import { memberById } from '../data/derive';
import { useFamily, type WithId } from '../data/family';
import { NoteForm } from './forms';
import { starsText } from '../i18n/stars';

/**
 * The family pinboard on "Heute" (and so on the wall): one slim line per note, newest first.
 * Nothing pinned → nothing shown; pinning happens via "＋ → Notiz".
 */
export function Pinboard() {
  const f = useFamily();
  const lens = useLens();
  const canPlan = lens.canParent || f.role === 'teen';
  const [open, setOpen] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const note = f.notes.find((n) => n.id === open);
  // parents and teens tidy up; everyone else changes only what they pinned themselves
  const canEdit = canPlan || (!!note?.createdBy && note.createdBy === f.meId);
  if (!f.notes.length) return null;

  const close = () => {
    setOpen(null);
    setEditing(false);
    setConfirm(false);
  };
  return (
    <section aria-label={starsText.pinboard}>
      {/* one slim line per note (1.10: "Heute" got too full) – two columns on wide screens */}
      <ul className="grid grid-cols-1 gap-gap-xs sm:grid-cols-2">
        {f.notes.map((n) => (
          <li key={n.id} className="flex min-w-0">
            <NoteCard {...noteProps(f, n)} variant="strip" onPress={() => setOpen(n.id)} />
          </li>
        ))}
      </ul>
      <Sheet
        isOpen={!!note}
        onOpenChange={(o) => !o && close()}
        title={editing ? starsText.editNote : starsText.pinboard}
        isDismissable={!editing}
      >
        {note && editing && <NoteForm note={note} onDone={close} />}
        {note && !editing && (
          <div className="flex flex-col gap-gap-lg">
            <NoteCard {...noteProps(f, note)} />
            {canEdit && (
              <div className="flex flex-wrap gap-gap-sm">
                <Button variant="secondary" icon={PencilSimple} onPress={() => setEditing(true)}>
                  {starsText.change}
                </Button>
                {confirm ? (
                  <Button
                    variant="danger"
                    icon={Trash}
                    onPress={() => {
                      void deleteNote(f.householdId, note.id);
                      close();
                    }}
                  >
                    {starsText.reallyRemove}
                  </Button>
                ) : (
                  <Button variant="quiet" icon={Trash} onPress={() => setConfirm(true)}>
                    {starsText.remove}
                  </Button>
                )}
              </div>
            )}
            <Text size="sm" tone="muted">
              {canEdit ? starsText.removedForAll : starsText.whoPins}
            </Text>
          </div>
        )}
      </Sheet>
    </section>
  );
}

function noteProps(f: ReturnType<typeof useFamily>, n: WithId<Note>) {
  const author = memberById(f, n.createdBy);
  return {
    text: n.text,
    author: author?.name,
    color: author?.color ?? 'sun',
    until: n.until
      ? n.until === f.today
        ? starsText.onlyToday
        : n.until === addDays(f.today, 1)
          ? starsText.untilTomorrow
          : starsText.until(dayLabel(f, n.until, true).label)
      : undefined,
  };
}
